/**
 * Chart.js instance lifecycle, shared by every Chart.js-backed composable.
 *
 * Owns the one thing both `useImpactChart.ts` and `useMetricChart.ts` need
 * regardless of what colour or dataset opinions they carry: a `Ref<Chart|null>`,
 * a lazy `import('chart.js/auto')` so Chart.js never lands in the initial
 * bundle, destroy-then-recreate on render, and destruction on unmount so
 * nothing leaks.
 */

import { ref, onUnmounted, type Ref } from 'vue';
import type { Chart, ChartConfiguration } from 'chart.js';

export function useChartLifecycle(canvasRef: Ref<HTMLCanvasElement | null>) {
  const chartInstance: Ref<Chart | null> = ref(null);

  const destroy = () => {
    if (chartInstance.value) {
      chartInstance.value.destroy();
      chartInstance.value = null;
    }
  };

  const render = async (config: ChartConfiguration | null) => {
    if (!config || !canvasRef.value) {
      destroy();
      return;
    }
    destroy();
    const { Chart: ChartJS } = await import('chart.js/auto');
    chartInstance.value = new ChartJS(canvasRef.value, config as never);
  };

  onUnmounted(destroy);

  return { chartInstance, render, destroy };
}

export default useChartLifecycle;
